import { ReactElement, ReactNode } from 'react';
import Modal from 'react-modal';
import styles from './ui-drawer.module.scss';
import { UIText, UITextProps } from '../ui-text';
import { UIIcon } from '../ui-icon';
import { UIPressableAreaButtonLess } from '../ui-pressable-area';
import { charcoal } from '../../styles';

/** Kept in step with the CSS transition durations in ui-drawer.module.scss. */
const ANIMATION_MS = 250;

export type UIDrawerProps = {
    isOpen: boolean;
    onClose: () => void;
    children: ReactNode;
    /** Widens the panel on large screens, for denser content. */
    wide?: boolean;
};

/**
 * A panel that slides in from the right, for detail that would interrupt the
 * page if it were a centred modal. Built on the same react-modal primitive as
 * UIModal, so overlay click and ESC close it for free.
 */
export function UIDrawer(props: UIDrawerProps): ReactElement {
    const { isOpen, onClose, children, wide = false } = props;

    const base = [styles.container];
    if (wide) {
        base.push(styles.containerWide);
    }

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={onClose}
            // react-modal applies the afterOpen/beforeClose classes on their own
            // ticks, which is what drives the slide; closeTimeoutMS holds the
            // panel mounted long enough for the exit transition to play.
            closeTimeoutMS={ANIMATION_MS}
            className={{
                base: base.join(' '),
                afterOpen: styles.containerAfterOpen,
                beforeClose: styles.containerBeforeClose
            }}
            overlayClassName={{
                base: styles.overlay,
                afterOpen: styles.overlayAfterOpen,
                beforeClose: styles.overlayBeforeClose
            }}
        >
            {children}
        </Modal>
    );
}

export type UIDrawerHeaderProps = {
    title: string;
    titleProps?: UITextProps;
    subtitle?: string;
    subtitleProps?: UITextProps;
    /** Rendered between the title block and the close button. */
    rightNode?: ReactNode;
    onClose: () => void;
};

export function UIDrawerHeader(props: UIDrawerHeaderProps): ReactElement {
    const { title, titleProps, subtitle, subtitleProps, rightNode, onClose } =
        props;

    return (
        <div className={styles.header}>
            <div className={styles.headerText}>
                <UIText variant="h3" bold {...titleProps}>
                    {title}
                </UIText>
                {subtitle && (
                    <UIText type="default2" variant="small" {...subtitleProps}>
                        {subtitle}
                    </UIText>
                )}
            </div>
            {rightNode}
            <button className={styles.closeButton} onClick={onClose}>
                <UIPressableAreaButtonLess width={20} height={20}>
                    <UIIcon name="x" size={20} color={charcoal} />
                </UIPressableAreaButtonLess>
            </button>
        </div>
    );
}

export type UIDrawerContentProps = {
    children: ReactNode;
};

export function UIDrawerContent(props: UIDrawerContentProps): ReactElement {
    return <div className={styles.content}>{props.children}</div>;
}
